<template>
  <div class="tes-employee-form">
    <form v-scroll-spy="{ allowNoActive: true }" class="vertical" data-scroll-spy-id="sub-tab">
      <section v-if="canViewUserAccount">
        <h1>{{ $t('tesEmployees.labels.userAccount') }}</h1>
        <div>
          <router-link :to="`/users/${formData.user.reference_number}`"
            ><font-awesome-icon icon="user"></font-awesome-icon>
            {{ formData.user.username }}</router-link
          >
          <span v-if="!formData.user.is_active" class="inactive">
            {{ $t('users.labels.inactiveUser') }}
          </span>
        </div>
      </section>
      <section>
        <h1>{{ $t('tesEmployees.labels.personalDetails') }}</h1>

        <div class="form-control">
          <label>{{ $t('tesEmployees.labels.lastName') }}</label>
          <InputField
            v-model="formData.user.last_name"
            type="text"
            class="form-input"
            @change="onChange"
          />
        </div>

        <div class="form-control">
          <label>{{ $t('tesEmployees.labels.firstName') }}</label>
          <InputField
            v-model="formData.user.first_name"
            type="text"
            class="form-input"
            @change="onChange"
          />
        </div>

        <div class="form-control">
          <label>{{ $t('tesEmployees.labels.preferredName') }}</label>
          <InputField
            v-model="formData.user.preferred_name"
            type="text"
            class="form-input"
            @change="onChange"
          />
        </div>

        <div class="form-control">
          <label>{{ $t('tesEmployees.labels.branch') }}</label>
          <DropdownSelect
            :menu-items="branchNames"
            :selected-item="formData.branch_name"
            class="blue"
            @select-item="onBranchChange"
          />
        </div>

        <div class="break" />

        <div class="form-control">
          <label>{{ $t('tesEmployees.labels.emails') }}</label>
          <InputField
            v-model="formData.user.email"
            type="text"
            class="form-input"
            field-icon="envelope"
            @change="onChange"
          />
          <InputField
            v-model="formData.user.email_alt"
            type="text"
            class="form-input"
            field-icon="envelope"
            @change="onChange"
          />
        </div>
        <div class="form-control">
          <label>{{ $t('tesEmployees.labels.phoneNumbers') }}</label>
          <div class="multi-inline">
            <InputField
              v-model="formData.user.phone1"
              type="text"
              class="form-input"
              field-icon="phone-square"
              @change="onChange"
            />
            <DropdownSelect
              :menu-items="phoneTypeOptions"
              :selected-item="formData.user.phone1_type"
              class="blue"
              @select-item="onPhone1TypeChange"
            />
          </div>
          <div class="multi-inline">
            <InputField
              v-model="formData.user.phone2"
              type="text"
              class="form-input"
              field-icon="phone-square"
              @change="onChange"
            />
            <DropdownSelect
              :menu-items="phoneTypeOptions"
              :selected-item="formData.user.phone2_type"
              class="blue"
              @select-item="onPhone2TypeChange"
            />
          </div>
          <div class="multi-inline">
            <InputField
              v-model="formData.user.phone3"
              type="text"
              class="form-input"
              field-icon="phone-square"
              @change="onChange"
            />
            <DropdownSelect
              :menu-items="phoneTypeOptions"
              :selected-item="formData.user.phone3_type"
              class="blue"
              @select-item="onPhone3TypeChange"
            />
          </div>
        </div>
      </section>
      <section class="role-section">
        <h1>{{ $t('tesEmployees.labels.roles') }}</h1>
        <div>
          <ul>
            <li v-for="role in currentRoles" :key="role">{{ role }}</li>
          </ul>
        </div>
        <div class="admin-info">
          <span><font-awesome-icon icon="info-circle"/></span>
          <span> {{ $t('tesEmployees.details.info.adminCanEdit') }}</span>
        </div>
      </section>
      <!-- commission profiles only for recruiter and account manager roles-->
      <section
        v-if="currentRoles.includes('Recruiters') || currentRoles.includes('Account Managers')"
      >
        <div class="form-control">
          <div class="list-header">
            {{ $t('tesEmployees.labels.commissionProfiles') }}
            <span @click="isCommissionFormVisible = !isCommissionFormVisible">+</span>
          </div>
          <tol-table :table-data="commissionProfiles" :columns="commissionColumns">
            <template v-slot:tableHeader>
              <th>{{ $t('tesEmployees.labels.branch') }}</th>
              <th>{{ $t('tesEmployees.labels.division') }}</th>
              <th>{{ $t('tesEmployees.labels.description') }}</th>
              <th>{{ $t('tesEmployees.labels.commission_number') }}</th>
              <th></th>
            </template>
            <template v-slot:click-action="{ value }"> </template>
          </tol-table>

          <div v-show="isCommissionFormVisible" class="commission-form">
            <DropdownSelect
              :menu-items="branchNames"
              :selected-item="branchNames[0]"
              class="blue"
              @select-item="changeNewBranch($event)"
            >
              <template v-slot:selectedItem="{ value }">
                {{ value }}
              </template>
              <template v-slot:dropdownItem="{ value }">
                {{ value }}
              </template>
            </DropdownSelect>

            <DropdownSelect
              :menu-items="divisions"
              :selected-item="divisions[0]"
              :filter-on="['abbr']"
              class="blue"
              @select-item="changeNewDivision($event)"
            >
              <template v-slot:selectedItem="{ value }">
                {{ value.abbr }}
              </template>
              <template v-slot:dropdownItem="{ value }">
                {{ value.abbr }}
              </template>
            </DropdownSelect>

            <input v-model="newCommissionProfile.description" type="text" name="descripton" />
            <input
              v-model="newCommissionProfile.commission_number"
              type="text"
              name="commission_number"
            />

            <button type="button" @click="addOnScreenValue(newCommissionProfile)">
              Add
            </button>
          </div>
        </div>
      </section>
      <!-- show departments if its payroll -->
      <section v-if="currentRoles.includes('Payroll')">
        <h1>Departments</h1>
        <div class="department-container">
          <tol-multi-select
            class="blue"
            :current-items="currentItems"
            :item-list="departments"
            @item-change="addRemoveDepartment($event)"
          >
          </tol-multi-select>
        </div>
      </section>

      <div class="save-action">
        <tol-alt-button
          label="save"
          icon-name="save"
          :disabled="!isOnScreenDataModified && !!formData.reference_number"
          @click="doTesEmployeeSave(formData.reference_number ? 'SAVE' : 'CREATE')"
        >
        </tol-alt-button>
      </div>
    </form>
    <update-modal v-if="isProcessFormBtnClicked">
      <template v-slot:modalData>
        <vue-simple-spinner size="massive" />
      </template>
    </update-modal>
  </div>
</template>

<script>
import DropdownSelect from '@/app/components/DropdownSelect';
import InputField from '@/app/components/InputField';
import TolMultiSelect from '@/app/components/TolMultiSelect';
import { AppGetters } from '@/app/app.keys';
import TolAltButton from '@/app/components/TolAltButton';
import { clone } from 'ramda';
import TolTable from '@/app/components/TolTable';
import { TesEmployeeDetailsActions } from '../tes-employee-details.keys';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import { tesEmployeeDetailsService } from '@/app/features/tes-employees/features/tes-employee-details/tes-employee-details.service';
import { TolErrorsGetters } from '@/app/features/tol-errors/tol-errors.keys';
import UpdateModal from '@/app/components/UpdateModal';
export default {
  name: 'TesEmployeeDetailsForm',

  components: {
    DropdownSelect,
    InputField,
    TolAltButton,
    TolTable,
    TolMultiSelect,
    UpdateModal
  },

  props: {
    tesEmployee: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      formData: {},
      isOnScreenDataModified: false,
      isProcessFormBtnClicked: false,
      isCommissionFormVisible: false,
      commissionColumns: ['branch_name', 'division_abbr', 'description', 'commission_number'],
      newCommissionProfile: {
        branch_name: '',
        division_abbr: '',
        description: '',
        commission_number: 0
      }
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    canViewUserAccount() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['USER_ACCOUNT'] &&
        !!this.currentUser.permissions['USER_ACCOUNT']['VIEW']
      );
    },
    phoneTypeOptions() {
      return this.$store.getters[AppGetters.PhoneTypes];
    },
    currentItems() {
      return this.formData.departments.map(d => d.department_code) || [];
    },
    currentRoles() {
      return this.formData.roles.map(r => r.role_name) || [];
    },
    departments() {
      return this.$store.getters[AppGetters.Departments].map(d => d.code);
    },
    branchNames() {
      return this.$store.getters[AppGetters.BranchNames];
    },
    commissionProfiles: {
      get() {
        return this.formData.commission_profiles || [];
      }
    },
    divisions() {
      return this.$store.getters[AppGetters.DivisionAbbrs];
    },
    formErrors() {
      return this.$store.getters.TesEmployeesValidationErrors;
    },
    currentSaveStatus() {
      return this.$store.getters[TolErrorsGetters.HasErrors] ? 'error' : 'success';
    }
  },
  watch: {
    tesEmployee: {
      immediate: true,
      handler() {
        this.formData = clone(this.tesEmployee);
      }
    }
  },
  methods: {
    onChange() {
      this.isOnScreenDataModified = true;
    },

    onPhone1TypeChange(event) {
      this.formData.user.phone1_type = event;
      this.isOnScreenDataModified = true;
    },
    onPhone2TypeChange(event) {
      this.formData.user.phone2_type = event;
      this.isOnScreenDataModified = true;
    },
    onPhone3TypeChange(event) {
      this.formData.user.phone3_type = event;
      this.isOnScreenDataModified = true;
    },
    onBranchChange(event) {
      this.formData.branch_name = event;
      this.isOnScreenDataModified = true;
    },

    addOnScreenValue(newCommissionProfile) {
      let commission_number = newCommissionProfile.commission_number;
      let isCommissionAdded = false;
      if (this.formData.commission_profiles) {
        isCommissionAdded = this.formData.commission_profiles.some(
          element => element.commission_number == commission_number
        );
      }
      if (isCommissionAdded) {
        this.$store.dispatch(TolErrorsActions.SetError, {
          message: this.$t('tesEmployees.details.errors.commissionError')
        });
      } else {
        tesEmployeeDetailsService.validateCommissionNumber(commission_number).then(response => {
          if (response.data.message == 'Success') {
            let newCommission = {};
            this.isCommissionFormVisible = false;
            if (newCommissionProfile.branch_name === '') {
              newCommissionProfile.branch_name = this.branchNames[0];
            }
            if (newCommissionProfile.division_abbr === '') {
              newCommissionProfile.division_abbr = this.divisions[0].abbr;
            }
            newCommission = Object.assign({}, newCommissionProfile);

            if (!this.formData.commission_profiles) {
              this.formData['commission_profiles'] = new Array();
            }
            this.formData.commission_profiles.push(newCommission);
            this.isOnScreenDataModified = true;
            this.newCommissionProfile.description = '';
            this.newCommissionProfile.commission_number = 0;
          } else {
            const messages = Object.values(response.data.errors);
            let items = [];
            for (let item of messages) {
              items = items.concat(item);
            }

            this.$store.dispatch(TolErrorsActions.SetError, { items });
          }
        });
      }
    },
    changeNewDivision(event) {
      this.newCommissionProfile.division_abbr = event.abbr;
    },
    changeNewBranch(event) {
      this.newCommissionProfile.branch_name = event;
    },
    addRemoveDepartment(evt) {
      const updatedDepartments = this.departments.filter(d => evt.includes(d));
      this.formData.departments = updatedDepartments.map(d => {
        return { department_code: d };
      });
      this.isOnScreenDataModified = true;
    },
    doTesEmployeeSave(action) {
      if (this.isOnScreenDataModified) {
        this.isProcessFormBtnClicked = true;
        this.$store.dispatch(TesEmployeeDetailsActions.SaveTesEmployee, {
          tesEmployeeData: this.formData,
          action
        });
        window.setTimeout(() => {
          this.isProcessFormBtnClicked = false;
        }, 2000);
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';

.tes-employee-form {
  form {
    width: 100%;
    section {
      margin-bottom: 20px;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;

      h1 {
        padding-top: 15px;
        border-bottom: 1px solid $row-border;
      }
      h1,
      h2 {
        flex: 0 100%;
      }
      a {
        text-decoration: none;
      }

      .break {
        width: 100%;
        height: 0;
      }

      .inactive {
        color: $red;
        border: 1px solid $red;
        padding: 0 5px;
        margin-left: 5px;
      }

      .form-control {
        padding: 10px 0;
        margin: 1em;
        flex: 1;
        > label {
          display: block;
          font-weight: bold;
        }

        .multi-inline {
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;

          > * {
            margin-left: 1em;
          }
          > *:first-child {
            margin-left: 0;
          }
          .dropdown-container {
            width: 20%;
          }
        }

        .list-header {
          display: flex;
          justify-content: space-between;
          border-bottom: 4px solid $row-border;
          font-weight: bold;
          span {
            display: inline-block;
            font-size: 1.2em;
            font-weight: bold;
            width: 20px;
            height: 20px;
            line-height: 20px;
            border-radius: 100%;
            text-align: center;
            &:hover {
              cursor: pointer;
              background-color: $light-grey-2;
            }
            &.history {
              display: none;
            }
          }
        }

        .commission-form {
          margin-top: 20px;
          display: flex;
          justify-content: space-between;
          input,
          textarea,
          select {
            width: auto;
            display: inline-block;

            border: 1px solid $mid-blue;
          }
          .dropdown-container {
            width: 15%;
          }
        }
      }
    }

    .form-input {
      border-bottom: 1px solid $blue;
      font-size: 1em;
      height: inherit;
      &:focus {
        outline: none;
      }
    }

    .save-action {
      display: flex;
      justify-content: flex-end;
      .wrapper {
        .btn-container {
          background-color: red;
        }
      }
    }
  }

  .nowrap {
    white-space: nowrap;
  }
  .department-container {
    display: flex;
    flex-basis: 50%;
    .multi-select-container {
      //border: 2px solid $row-border;
      padding: 8px;
      width: 50%;
    }
  }

  .role-section {
    display: flex;
    flex-direction: column;
    .admin-info {
      padding: 12px 5px;
      width: auto;
      background-color: $mid-blue;
      span {
        margin: 0 8px;
      }
    }

    ul {
      list-style-type: square;
      padding: 8px;
      margin: 8px;
    }
  }
}
</style>
